import React, { useState, useEffect } from 'react';
import { FiSend, FiAlertCircle } from 'react-icons/fi';
import Modal from '../common/Modal';
import ButtonLoading from '../ButtonLoading';
import { userRequest } from '../../utils/requestMethods';

const RecordExpenseModal = ({ isOpen, onClose, schoolId, onCreated }) => {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().slice(0, 10));
  const [beneficiary, setBeneficiary] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!schoolId) return;
    const fetchCategories = async () => {
      try {
        const { data } = await userRequest.get(`/schools/${schoolId}/finance/expense-categories`);
        const merged = [...(data.system || []), ...(data.custom || [])];
        setCategories(merged);
      } catch (err) {
        console.log(err);
      }
    };
    fetchCategories();
  }, [schoolId]);

  const reset = () => {
    setCategory('');
    setTitle('');
    setAmount('');
    setBeneficiary('');
    setDescription('');
    setError('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!category || !title || !amount || !expenseDate || !beneficiary) {
      setError('Please fill all the required fields.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await userRequest.post(`/schools/${schoolId}/expenses`, {
        category,
        title,
        amount: Number(amount),
        expense_date: new Date(expenseDate).toISOString(),
        beneficiary,
        ...(description && { description }),
      });
      onCreated?.();
      reset();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to record expense. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Record Offline Expense" icon={<FiSend className="text-indigo-600 text-lg" />}>
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mb-4">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-xl px-4 py-3 text-sm mb-4">
        <FiAlertCircle className="mt-0.5 shrink-0" />
        <span>
          This is an informational record only. It will not reduce the selected fund, affect the dashboard, or appear in reconciliation.
        </span>
      </div>

      <form onSubmit={submitHandler} className="space-y-4">
        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">Category</label>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
          >
            <option value="" disabled>Select a category</option>
            {categories.map(cat => (
              <option key={cat.slug} value={cat.slug}>{cat.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">Title</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Termly stationery purchase"
            className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">Amount (₦)</label>
          <input
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="e.g. 25000"
            className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">Expense Date</label>
          <input
            type="date"
            value={expenseDate}
            onChange={e => setExpenseDate(e.target.value)}
            className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">Beneficiary</label>
          <input
            type="text"
            value={beneficiary}
            onChange={e => setBeneficiary(e.target.value)}
            placeholder="e.g. ABC Stationery Ltd"
            className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">Description (optional)</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            maxLength={1000}
            rows={2}
            className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
          />
        </div>

        {loading ? (
          <ButtonLoading styles="w-full text-white bg-indigo-500 font-medium rounded-full text-sm px-5 py-2.5 text-center" />
        ) : (
          <button
            type="submit"
            className="w-full text-white bg-indigo-600 hover:bg-indigo-700 font-medium rounded-full text-sm px-5 py-2.5 text-center transition-colors"
          >
            Save Offline Record
          </button>
        )}
      </form>
    </Modal>
  );
};

export default RecordExpenseModal;
